import { Injectable, Optional, Inject } from "@angular/core";
import {
  IContextService,
  PershingTranslationService,
  LogService,
  ServiceRouter,
  PershingError,
  DomainContext,
  CoreConstants,
  GroupContextValue,
  PershingHttpUtil,
  IAuthorizationService,
} from "@pershing/pershing-core";
import { Observable, BehaviorSubject, Subscription } from "rxjs";
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from "@angular/common/http";

import { UglList } from "../model/Ugl/uglList.model";
import { QuoteRef, QuoteToggleService } from "@pershing/nxi-ng-quotetoggle";
import { GainLossConstants } from "../utils/gainloss.constants";
import { UGLServiceDefinition } from "../utils/gainloss.service.definition";

@Injectable({
  providedIn: "root",
})
export class UnRealizedGainLossService {
  ugl$: Observable<any>;
  error$: Observable<PershingError>;
  contextChanged$: Observable<boolean>;
  selectedView$: Observable<string>;
  exportUgl$: Observable<UglList>;
  exportClientUgl$: Observable<UglList>;
  selectedRowIndexData$: Observable<any>;
  selectedRowDetailData$: Observable<any>;
  setFilterBySymbol$: Observable<any>;
  setTerm$: Observable<string>;

  quoteType: string = GainLossConstants.DELAYED;
  realtimeQuote$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  asOfDate: string = new Date().toISOString();

  private contextSubscription: Subscription;
  private errorSubscription: Subscription;

  private _ugl: BehaviorSubject<any>;
  private _error: BehaviorSubject<PershingError>;
  private _contextChanged: BehaviorSubject<boolean>;
  private _selectedGainLoss: BehaviorSubject<string>;
  private _exportUgl: BehaviorSubject<UglList>;
  private _exportClientUgl: BehaviorSubject<UglList>;
  private _selectedRowIndexData: BehaviorSubject<any>;
  private _selectedRowDetailData: BehaviorSubject<any>;
  private _setFilterBySymbol: BehaviorSubject<any>;
  private _setTerm: BehaviorSubject<string>;

  private uglServiceBaseUrl: string;

  contextChanged: boolean;
  selectedGainLoss: string;
  acctNumber: string = "";
  acctRef: any;
  uglUri: string = "/accounts/";
  ibd: string;
  uglSummary: any;
  searchWord: string;
  term: string;
  public dataStore: {
    asOfDate: string;
    ugl: any;
    exportUgl: UglList;
    exportClientUgl: UglList;
    selectedRowIndexData: any;
    selectedRowDetailData: any;
  };
  quoteReferenceSub: any;
  quoteRef: QuoteRef;
  private isUGLSummaryLoadUpdate = new BehaviorSubject(true);
  isUglRTQuoteEntitled: boolean;

  constructor(
    private logService: LogService,
    private translationService: PershingTranslationService,
    private http: HttpClient,
    private contextService: IContextService,
    private serviceRouter: ServiceRouter,
    private quoteToggleService: QuoteToggleService,
    @Optional() @Inject("AppSettings") appSettings: any,
    private authorizationService: IAuthorizationService
  ) {
    this.ibd = appSettings.ibdConfigMap.IBD;
    this.initializeUglVariables();
    this.contextService.selectedDomainContext$.subscribe(
      (domainContext: DomainContext<any>) => {
        let contextValue = domainContext.value;
        if (contextValue instanceof GroupContextValue) {
          this.acctNumber = contextValue?.groupGUID;
          this.uglUri = "/groups/";
        } else {
          this.acctNumber = contextValue?.accountNumber;
          this.uglUri = "/accounts/";
        }
        // this.invokeUglService(
        //   this.acctNumber,
        //   this.selectedGainLoss,
        //   this.searchWord,
        //   this.quoteType,
        //   this.term
        // );
      }
    );
    this.invokeQuoteReferenceAction();
  }
  private initializeUglVariables(): void {
    if (this.translationService) {
      this.translationService.addTranslationFolder(
        "@pershing/nxi-ng-gainloss",
        "node_modules/@pershing/nxi-ng-gainloss/lib"
      );
    }
    this.isUglRTQuoteEntitled = !!this.authorizationService.isEntitled(
      "UGL_WITH_RT_QUOTES"
    )
      ? this.authorizationService.isEntitled("UGL_WITH_RT_QUOTES")
      : false;
    this.quoteRef = {
      pageName: "ugl",
      realTimeRefOn: false,
      asOfDate: this.asOfDate,
      agreementSigned: true,
      quoteAction: "",
      isRTQuoteEntitled: this.isUglRTQuoteEntitled,
    };
    this.uglServiceBaseUrl = this.serviceRouter.getServiceRouteURL(
      UGLServiceDefinition.getInstance()
    );
    this.logService.info(this.uglServiceBaseUrl);
    this.initializeObservables();
    this.uglSummary = this.getDefaultUglSummaryResult().uglTotal;
    this._contextChanged = new BehaviorSubject<boolean>(this.contextChanged);
    this.contextChanged$ = this._contextChanged.asObservable();
    this._setFilterBySymbol = new BehaviorSubject<any>(this.searchWord);
    this.setFilterBySymbol$ = this._setFilterBySymbol.asObservable();
    this._setTerm = new BehaviorSubject<string>(this.term);
    this.setTerm$ = this._setTerm.asObservable();
  }

  private invokeQuoteReferenceAction(): void {
    this.quoteReferenceSub = this.quoteToggleService.quoteReference$.subscribe(
      (quoteReferenceList) => {
        if (quoteReferenceList) {
          quoteReferenceList.forEach((item) => {
            if (item.pageName == this.quoteRef.pageName) {
              item.isRTQuoteEntitled = this.quoteRef.isRTQuoteEntitled;
              this.quoteType = item.realTimeRefOn
                ? GainLossConstants.REALTIME
                : GainLossConstants.DELAYED;
              this.realtimeQuote$.next(item.realTimeRefOn);
              // Only on quote refresh or toggle.
              if (!!item.quoteAction) {
                this.invokeUglService(
                  this.acctNumber,
                  this.selectedGainLoss,
                  this.searchWord,
                  this.quoteType,
                  this.term
                );
              }
            }
          });
        }
      }
    );
  }

  getUGLSummaryLoadUpdate(): Observable<boolean> {
    return this.isUGLSummaryLoadUpdate.asObservable();
  }

  setUGLSummaryLoadupdate(value: boolean) {
    this.isUGLSummaryLoadUpdate.next(value);
  }

  private initializeObservables(): void {
    this.dataStore = {
      asOfDate: null,
      ugl: null,
      exportUgl: null,
      exportClientUgl: null,
      selectedRowDetailData: null,
      selectedRowIndexData: null,
    };
    this.contextChanged = false;
    this._ugl = new BehaviorSubject({});
    this.ugl$ = this._ugl.asObservable();
    this._error = new BehaviorSubject<PershingError>(null);
    this.error$ = this._error.asObservable();
    this._selectedRowIndexData = new BehaviorSubject(null);
    this.selectedRowIndexData$ = this._selectedRowIndexData.asObservable();
    this._exportUgl = new BehaviorSubject(null);
    this.exportUgl$ = this._exportUgl.asObservable();
    this._exportClientUgl = new BehaviorSubject(null);
    this.exportClientUgl$ = this._exportClientUgl.asObservable();
    this._selectedRowDetailData = new BehaviorSubject(null);
    this.selectedRowDetailData$ = this._selectedRowDetailData.asObservable();
  }

  public getDefaultUglSummaryResult() {
    return {
      errorCode: 0,
      errorDescription: "SUCCESS",
      errorMessage: null,
      domain: CoreConstants.ACCOUNT,
      uglTotal: {
        totalMarketValue: 96210.9,
        totalCost: 10560.56,
        totalGain: 157000.0,
        totalGainLoss: 152000.0,
        totalGainLossPct: 0,
        totalLongTermGainLoss: 0.0,
        totalLoss: -5000.0,
        totalShortTermGainLoss: 0.0,
      },
    };
  }

  async invokeUglService(
    acct: any,
    view: string,
    searchWord: string,
    quoteType: string,
    term: string,
    uglUrl?: string
  ) {
    let params = new HttpParams();
    let searchWordParam = this.setFilterBySymbolValue(searchWord);
    let termParams = this.setTermValue(term);
    let url: any;
    if (uglUrl != "/accounts/") {
      url =
        this.uglServiceBaseUrl +
        uglUrl +
        acct +
        "?quoteType=" +
        this.quoteType +
        "&isCashIncluded=true&groupview=true&pageSize=2000" +
        termParams +
        searchWordParam;
    } else {
      url =
        this.uglServiceBaseUrl +
        uglUrl +
        acct +
        "?quoteType=" +
        this.quoteType +
        "&isCashIncluded=true&pageSize=2000" +
        termParams +
        searchWordParam;
    }
    const headers = PershingHttpUtil.secureUrl();
    await this.http
      .get(url, { headers: headers, params, observe: "response" })
      .subscribe(
        (response: HttpResponse<any>) => {
          if (response) {
            this.handleUGLResponse(response);
          }
        },
        (error: HttpErrorResponse) => {
          console.log("error while fetching UGL summary");
          this.handleUGLError(error);
        }
      );
  }

  public setFilter(subjectName, value: any) {
    if (subjectName === "_contextChanged") {
      this.setcontextChanged(value);
    } else if (subjectName === "_selectedGainLoss") {
      this.setSelectedGainLossFilter(value);
    }
  }

  private setcontextChanged(value: boolean): void {
    if (this.contextChanged !== value) {
      this.contextChanged = value;
      this._contextChanged.next(value);
    }
  }

  private setSelectedGainLossFilter(value: string): void {
    if (this.selectedGainLoss !== value) {
      this.selectedGainLoss = value;
      this._selectedGainLoss.next(value);
    }
  }

  setFilterBySymbolValue(searchWord: string) {
    let searchWordParam: string = "";
    if (searchWord && searchWord !== undefined) {
      searchWordParam = `&filter=securityDetail.identifier%5bcontains%5d${searchWord}`;
    }
    return searchWordParam;
  }

  setTermValue(term: string): string {
    let termParams: string = "";
    if (term !== undefined) {
      termParams = `&term=${term}`;
    }
    return termParams;
  }

  setFilterBySymbol(searchWord: string) {
    this.searchWord = searchWord;
    this.invokeUglService(
      this.acctNumber,
      this.selectedGainLoss,
      this.searchWord,
      this.quoteType,
      this.term
    );
    this._setFilterBySymbol.next(searchWord);
  }

  setError(errorMessage: string, errorCode?: string): void {
    this._error.next(
      new PershingError(errorMessage, "UnrealizedGainlossService", errorCode)
    );
  }

  clearError(): void {
    this._error.next(null);
  }

  setSelectedRowDetailData(rowDetailData: any): void {
    this.dataStore.selectedRowDetailData = rowDetailData;
    this._selectedRowDetailData.next(
      Object.assign({}, this.dataStore).selectedRowDetailData
    );
  }

  /**
   * @method handleUGLResponse
   * Handle the ugl response when in the onNext callback
   * @param response
   */
  private handleUGLResponse(response: HttpResponse<any>): void {
    const data = response.body;
    const statusCode = response.status;
    this.resetUGL();
    if (statusCode === 204) {
      this.dataStore.ugl = {};
      this._ugl.next(Object.assign({}, this.dataStore).ugl);
      this.clearError();
    } else if (data.errorCode === 0) {
      let ugl: any = {};
      this.clearError();
      if (data.response) {
        // check if not null
        ugl = data.response;
        if (ugl.clientEntity && ugl.clientEntity.clientAccountType === "away") {
          this.setError(GainLossConstants.HELDAWAY_ERROR, "error");
        }
      }
      this.dataStore.ugl = ugl;
      this.quoteRef.asOfDate = this.dataStore.ugl.asOfDateInLocalDateTime;
      this.quoteRef.realTimeRefOn =
        this.quoteType === GainLossConstants.REALTIME;
      this.quoteRef.quoteAction = "";
      this.quoteToggleService.updateQuoteRef(this.quoteRef);
      this._ugl.next(Object.assign({}, this.dataStore).ugl);

      return this.dataStore.ugl;
    } else {
      this.logService.error("");
      this.setError(CoreConstants.SYSTEM_ERROR_MESSAGE, "error");
    }
  }

  /**
   * @method resetUGL
   * @desc Resets and clears the context.
   */
  private resetUGL(): void {
    // For an empty/cleared context:
    // 1. Clear the dataStore
    this.dataStore = {
      asOfDate: null,
      ugl: null,
      exportUgl: null,
      exportClientUgl: null,
      selectedRowIndexData: null,
      selectedRowDetailData: null,
    };
    // 2. Publish empty object to ugl$ Observable
    this._ugl.next({});
    // 3. Clear errors. Propagates to components so they won't display previously set error messages.
    this.clearError();
  }

  /**
   * @method handleUGLError
   * @desc Handle error when in the error callback
   * @param error
   */
  private handleUGLError(error: HttpErrorResponse): void {
    if (error) {
      if (error.status === 403) {
        this.logService.error(error);
        this.setError(GainLossConstants.INSUFFCIENT_ENTITLEMENT_ERROR, "403");
      } else {
        this.logService.error(error);
        this.setError(CoreConstants.SYSTEM_ERROR_MESSAGE, "500"); // Always display generic system error
      }
    }
  }

  public setTerm(value: any) {
    if (value === GainLossConstants.ACTIVE_SUMMARY.SHORT_TERM) {
      this.term = "S";
    } else if (value === GainLossConstants.ACTIVE_SUMMARY.LONG_TERM) {
      this.term = "L";
    } else {
      this.term = "";
    }
    this.invokeUglService(
      this.acctNumber,
      this.selectedGainLoss,
      this.searchWord,
      this.quoteType,
      this.term
    );
  }

  private;

  ngOnDestroy(): void {
    if (this.contextSubscription) {
      this.contextSubscription.unsubscribe();
    }
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }
    if (this.quoteReferenceSub) {
      this.quoteReferenceSub.unsubscribe();
    }
  }
}
